import {inject, Injectable} from '@angular/core';
import {MatSnackBar, MatSnackBarConfig} from '@angular/material/snack-bar';
import {map, Observable, take} from 'rxjs';

export interface ToastOptions {
  closeButton?: boolean;
  disableTimeOut?: boolean;
  positionClass?: 'toast-bottom-right' | 'toast-top-right';
  tapToDismiss?: boolean;
}

export interface ToastRef {
  onTap: Observable<void>;
  onHidden: Observable<void>;
}

type ToastKind = 'error' | 'info' | 'success' | 'warning';

@Injectable({providedIn: 'root'})
export class ToastService {
  private readonly snackBar = inject(MatSnackBar);
  private activeToast?: {message: string; ref: ToastRef};

  success(message: string): ToastRef {
    return this.open(message, 'success');
  }

  error(message: string): ToastRef {
    return this.open(message, 'error');
  }

  warning(message: string): ToastRef {
    return this.open(message, 'warning');
  }

  info(message: string, _title?: string, options: ToastOptions = {}): ToastRef {
    return this.open(message, 'info', options);
  }

  private open(message: string, kind: ToastKind, options: ToastOptions = {}): ToastRef {
    if (this.activeToast?.message === message) {
      return this.activeToast.ref;
    }

    const persistent = options.disableTimeOut === true;
    const config: MatSnackBarConfig = {
      duration: persistent ? undefined : 5000,
      horizontalPosition: 'right',
      verticalPosition: options.positionClass === 'toast-bottom-right' ? 'bottom' : 'top',
      panelClass: ['hypecast-toast', `hypecast-toast--${kind}`],
      politeness: kind === 'error' ? 'assertive' : 'polite',
    };
    const snackBarRef = this.snackBar.open(message, persistent && options.tapToDismiss === false ? 'Reload' : 'Dismiss', config);
    const ref: ToastRef = {
      onTap: snackBarRef.onAction(),
      onHidden: snackBarRef.afterDismissed().pipe(map((): void => undefined)),
    };

    this.activeToast = {message, ref};
    snackBarRef.afterDismissed().pipe(take(1)).subscribe(() => {
      if (this.activeToast?.ref === ref) {
        this.activeToast = undefined;
      }
    });

    return ref;
  }
}
